// Portal v2 Slice C1 — Account settings (2026-08-29).
//
// Spec'd day-1 (docs/apps/client-portal.md § "Account Settings"), never
// built until now. Three sections in the current visual language:
//   Profile  — display name (editable), company (read-only)
//   Email    — read-only; changing the login identity goes through the
//              account manager (re-verification flow is future scope)
//   Password — current → new → confirm; success signs the client out
//              everywhere (server revokes every session) and routes to
//              sign-in with a notice.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  changePortalPassword,
  fetchPortalAccount,
  updatePortalAccountName,
} from "@/lib/portal-account";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

const PASSWORD_ERROR_LABEL: Record<string, string> = {
  invalid_current_password: "That current password isn't right.",
  password_too_short: "New password needs at least 12 characters.",
  same_password: "New password must be different from the current one.",
};

export function AccountPage() {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: account } = useQuery({
    queryKey: ["portal-account"],
    queryFn: fetchPortalAccount,
  });

  // ── Profile name ──────────────────────────────────────────────────
  const [name, setName] = useState<string | null>(null);
  const [nameSaved, setNameSaved] = useState(false);
  const nameMutation = useMutation({
    mutationFn: (next: string) => updatePortalAccountName(next),
    onSuccess: () => {
      setNameSaved(true);
      void queryClient.invalidateQueries({ queryKey: ["portal-account"] });
    },
  });
  const nameValue = name ?? account?.name ?? "";
  const nameDirty =
    account !== undefined && nameValue.trim() !== account.name;

  // ── Password ──────────────────────────────────────────────────────
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwChanged, setPwChanged] = useState(false);
  const passwordMutation = useMutation({
    mutationFn: () =>
      changePortalPassword({
        currentPassword: currentPw,
        newPassword: newPw,
      }),
    onSuccess: () => {
      setPwError(null);
      setPwChanged(true);
      // Server revoked every session. Give the notice a beat, then clear
      // local auth state and land on sign-in. Deliberately NOT cleaned up
      // on unmount: the server-side session is already dead, so forcing
      // the sign-out even if the user navigated away is the correct
      // outcome, not a leak.
      window.setTimeout(() => {
        void logout().finally(() => navigate("/login"));
      }, 2500);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setPwError(
          (body?.error && PASSWORD_ERROR_LABEL[body.error]) ??
            "Something went wrong. Try again in a moment.",
        );
      } else {
        setPwError("Something went wrong. Try again in a moment.");
      }
    },
  });
  const pwMismatch = confirmPw.length > 0 && newPw !== confirmPw;
  const pwSubmittable =
    currentPw.length > 0 &&
    newPw.length >= 12 &&
    newPw === confirmPw &&
    !passwordMutation.isPending &&
    !pwChanged;

  if (!user) return null;

  return (
    <PortalLayout>
      <h1 className="mb-8 font-display text-3xl font-bold tracking-tight">
        Account
      </h1>

      {/* Profile */}
      <section className="mb-8 rounded-lg border border-cream-200 bg-white p-8 shadow-sm">
        <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
          Profile
        </h2>
        <div className="max-w-md space-y-4">
          <div>
            <label
              htmlFor="account-name"
              className="mb-1 block text-sm font-semibold text-brown-500"
            >
              Your name
            </label>
            <input
              id="account-name"
              type="text"
              value={nameValue}
              maxLength={200}
              onChange={(e) => {
                setName(e.target.value);
                setNameSaved(false);
              }}
              className="w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
            />
          </div>
          {account?.company && (
            <div>
              <div className="mb-1 text-sm font-semibold text-brown-500">
                Company
              </div>
              <div className="text-sm text-brown-400">{account.company}</div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!nameDirty || nameValue.trim().length === 0 || nameMutation.isPending}
              onClick={() => nameMutation.mutate(nameValue.trim())}
              className="rounded-md bg-ink px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
            >
              {nameMutation.isPending ? "Saving…" : "Save"}
            </button>
            {nameSaved && !nameDirty && (
              <span className="text-sm text-brown-400">Saved.</span>
            )}
            {nameMutation.isError && (
              <span className="text-sm text-rust-500">
                Couldn't save. Try again.
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Email */}
      <section className="mb-8 rounded-lg border border-cream-200 bg-white p-8 shadow-sm">
        <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
          Email
        </h2>
        <p className="text-sm text-brown-500">{account?.email ?? user.email}</p>
        <p className="mt-2 text-sm text-brown-400">
          Your email is how you sign in. To change it, reach out to your
          account manager.
        </p>
      </section>

      {/* Password */}
      <section className="rounded-lg border border-cream-200 bg-white p-8 shadow-sm">
        <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
          Password
        </h2>
        {pwChanged ? (
          <div className="rounded-md border border-cream-300 bg-cream-50 px-4 py-3 text-sm text-brown-500">
            Password changed. For your security you've been signed out
            everywhere — taking you to sign in…
          </div>
        ) : (
          <div className="max-w-md space-y-4">
            <div>
              <label
                htmlFor="current-password"
                className="mb-1 block text-sm font-semibold text-brown-500"
              >
                Current password
              </label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="new-password"
                className="mb-1 block text-sm font-semibold text-brown-500"
              >
                New password
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
              />
              <p className="mt-1 text-xs text-brown-300">
                At least 12 characters.
              </p>
            </div>
            <div>
              <label
                htmlFor="confirm-password"
                className="mb-1 block text-sm font-semibold text-brown-500"
              >
                Confirm new password
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full rounded-md border border-cream-300 bg-white p-2.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
              />
              {pwMismatch && (
                <p className="mt-1 text-xs text-rust-500">
                  Passwords don't match.
                </p>
              )}
            </div>
            {pwError && <p className="text-sm text-rust-500">{pwError}</p>}
            <div>
              <button
                type="button"
                disabled={!pwSubmittable}
                onClick={() => passwordMutation.mutate()}
                className="rounded-md bg-ink px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50"
              >
                {passwordMutation.isPending
                  ? "Changing…"
                  : "Change password"}
              </button>
              <p className="mt-2 text-xs text-brown-300">
                Changing your password signs you out on every device.
              </p>
            </div>
          </div>
        )}
      </section>
    </PortalLayout>
  );
}
